import {
  LayoutSettings,
  GridSettings,
  ArtboardPreset,
  ElementType,
  LayerStyleProperties,
  WithPotentialSharedStyle,
  StackProperties,
  LayerCorners,
  Appearance,
  VariantConfigurationEntry,
  VariantSetProperty,
} from './InspectorTypes'
import {
  SketchElementBase,
  SketchLayerElementBase,
  SketchGroupLayerElement,
  SketchImageLayerElement,
  SketchNonGraphicLayerElement,
  SketchShapeLayerElement,
  SketchSymbolInstanceLayerElement,
  SketchTextLayerElement,
} from './SketchElements'

/**
 * The object representing the whole Sketch scene.
 * The scene can have a page or a artboard element as a root.
 */
export interface SketchScene {
  /**
   * The root of the tree of elements composing the scene.
   */
  rootElement: SketchPageElement | SketchFrameElement
}

export type SketchElementType = SketchElement['type']

/**
 * An element of the Sketch scene tree.
 */
export type SketchElement =
  | SketchPageElement
  | SketchFrameElement
  | SketchLayerElement
  | SketchSectionElement

/**
 * An element representing a Sketch document page.
 */
export interface SketchPageElement extends SketchElementBase {
  type: ElementType.Page
}

export interface SketchSectionElement extends SketchLayerElementBase {
  type: ElementType.Section
  /**
   * A variant set is a frame-like container, so it can carry its own background
   * styling (fills, borders, shadows) that the inspector surfaces.
   */
  style: WithPotentialSharedStyle<LayerStyleProperties>
  /**
   * The variant properties this section defines when it is a variant set: each
   * property's key and its full set of possible values. Empty for a plain
   * section that is not a variant set.
   */
  variantProperties: VariantSetProperty[]
  /**
   * A section is a group-like container, so it can have a stack (flex) layout
   * applied to it. `null` when no layout is applied.
   */
  stack: StackProperties | null
}

export type SketchFrameElement =
  | SketchBasicFrameElement
  | SketchSymbolMasterFrameElement

/**
 * An element representing a Sketch document artboard that is not a symbol (master).
 */
export interface SketchBasicFrameElement extends SketchElementBase {
  type: ElementType.Artboard | ElementType.Graphic
  /** Settings of the artboard grid */
  gridSettings: GridSettings | undefined
  /** Settings of the artboard layout */
  layoutSettings: LayoutSettings | undefined
  /**
   * The artboard preset used. Can be empty if no preset was used.
   */
  preset: ArtboardPreset | undefined
  hasFragmentPending: boolean
  /**
   * The closest ancestor element that acts as a layout container: a frame, or
   * a variant set (Section), which is also a frame-like container.
   */
  containerAncestorElement?: SketchFrameElement | SketchSectionElement
  stack: StackProperties | null
  style: WithPotentialSharedStyle<LayerStyleProperties>
  rotation: number
  corners?: LayerCorners
  appearance?: Appearance
}
/**
 * An element representing a Sketch document artboard that is symbol (master).
 */
export interface SketchSymbolMasterFrameElement
  extends Omit<SketchBasicFrameElement, 'type'> {
  type: ElementType.SymbolMaster
  /**
   * In sketch symbols can be named using `/` to group similar symbols.
   * For example: Primitives/Buttons/Primary.
   * When using this convention the symbolMasterShortName is the last part of
   * the path. In the example above it would be "Primary".
   */
  symbolMasterShortName: string | undefined
  /**
   * In sketch symbols can be named using `/` to group similar symbols.
   * For example: Primitives/Buttons/Primary.
   * When using this convention the symbolMasterNamePath a list of all the sub-string except the last one,
   * when splitting on the "/". In the example above it would be ["Primitives", "Buttons"].
   */
  symbolMasterNamePath: string[] | undefined
  /**
   * When this master belongs to a variant set, the variant configuration it
   * represents: the ordered property/value entries (e.g. Type=Primary,
   * State=Enabled). Empty for standalone symbol masters.
   */
  variantConfiguration: VariantConfigurationEntry[]
}

/**
 * An element representing a Sketch document layer.
 */
export type SketchLayerElement =
  | SketchNonGraphicLayerElement
  | SketchTextLayerElement
  | SketchGroupLayerElement
  | SketchShapeLayerElement
  | SketchSymbolInstanceLayerElement
  | SketchImageLayerElement

/**
 * Represents the CSS properties to be extracted
 */
export type SketchLayerCSSProperties = Pick<
  SketchLayerElement | SketchFrameElement | SketchSectionElement,
  'layerSize' | 'style' | 'rotation' | 'corners'
> & {
  stack?: StackProperties | null
}

/**
 * A `SketchLayerCSSProperties` that is known to carry a style. Some layer types
 * (e.g. non-graphic layers) declare `style: undefined`, so narrowing to
 * `SketchLayerCSSProperties` alone is not enough for the CSS generator.
 */
export type SketchElementWithStyle = SketchLayerCSSProperties & {
  style: NonNullable<SketchLayerCSSProperties['style']>
}

/**
 * Narrows any scene element down to one the CSS generator can read.
 *
 * Callers hold a `SketchElement`, which includes pages and other elements that
 * carry no style at all, so they used to reach the CSS generator through an
 * unchecked `as SketchLayerCSSProperties` cast at each call site. This is the
 * single checked place that conversion happens.
 */
export function hasCSSProperties(
  element: SketchElement | SketchLayerCSSProperties
): element is SketchElementWithStyle {
  return 'style' in element && !!element.style
}
